import { GetScriptStatusResponseBodyType } from "@intelligems/ig-types";

export type AlertType =
  | "systemStatus"
  | "missingShopifyOrgId"
  | "scriptMissing"
  | "scriptOutdated"
  | "scriptIncomplete"
  | "scriptMultipleWithAppEmbed"
  | "scriptMultiple"
  | "scriptIncorrect"
  | "headlessMissing"
  | "headlessDeprecated"
  | "headlessOutdated"
  | "shopifyPermissions"
  | "customEventsEsm";

export interface AlertConfig {
  type: AlertType;
  priority: number;
  suppressedBy: AlertType[];
}

export const ALERT_CONFIGS: Record<AlertType, AlertConfig> = {
  systemStatus: {
    type: "systemStatus",
    priority: 1,
    suppressedBy: [],
  },
  missingShopifyOrgId: {
    type: "missingShopifyOrgId",
    priority: 2,
    suppressedBy: [],
  },
  scriptMissing: {
    type: "scriptMissing",
    priority: 3,
    suppressedBy: ["missingShopifyOrgId"],
  },
  scriptOutdated: {
    type: "scriptOutdated",
    priority: 4,
    suppressedBy: ["missingShopifyOrgId", "scriptMissing"],
  },
  scriptIncomplete: {
    type: "scriptIncomplete",
    priority: 5,
    suppressedBy: ["missingShopifyOrgId", "scriptMissing"],
  },
  scriptMultipleWithAppEmbed: {
    type: "scriptMultipleWithAppEmbed",
    priority: 5,
    suppressedBy: ["missingShopifyOrgId", "scriptMissing"],
  },
  scriptMultiple: {
    type: "scriptMultiple",
    priority: 5,
    suppressedBy: ["missingShopifyOrgId", "scriptMissing"],
  },
  scriptIncorrect: {
    type: "scriptIncorrect",
    priority: 6,
    suppressedBy: ["missingShopifyOrgId", "scriptMissing"],
  },
  headlessMissing: {
    type: "headlessMissing",
    priority: 7,
    suppressedBy: ["missingShopifyOrgId"],
  },
  headlessDeprecated: {
    type: "headlessDeprecated",
    priority: 7,
    suppressedBy: ["missingShopifyOrgId", "headlessMissing"],
  },
  headlessOutdated: {
    type: "headlessOutdated",
    priority: 7,
    suppressedBy: ["missingShopifyOrgId", "headlessMissing"],
  },
  shopifyPermissions: {
    type: "shopifyPermissions",
    priority: 8,
    suppressedBy: [],
  },
  customEventsEsm: {
    type: "customEventsEsm",
    priority: 9,
    suppressedBy: ["missingShopifyOrgId", "scriptMissing"],
  },
};

export type SystemAlertResponse = {
  enabled: boolean;
  dismissId: string;
  title: string;
  message: string;
  buttonText?: string | null;
  buttonUrl?: string | null;
  level?: "info" | "alert" | null; // default is "alert"
  expires_at?: string | null; // ISO timestamp or valid JS date string, overrides enabled when present
};

export interface AlertSystemData {
  isInstalled: boolean | undefined;
  scriptStatusType: GetScriptStatusResponseBodyType["type"] | undefined;
  scriptStatusExpected: string | undefined;
  scriptStatusFound: string | undefined;
  hasTemplateScript: boolean | undefined;
  hasProductThemeScript: boolean | undefined;
  hasRequiredShopifyScopes: boolean | undefined;
  isHeadless: boolean;
  pathname: string;
  hasSystemAlert: boolean;
}

/**
 * Computes the raw list of alerts based on system state.
 * Extracted as a pure function for testability.
 */
export function computeRawAlerts(alertData: AlertSystemData): AlertType[] {
  const alerts: AlertType[] = [];

  // Priority 1: SystemStatus (date-based)
  if (alertData.hasSystemAlert) {
    alerts.push("systemStatus");
  }

  // Priority 2: MissingShopifyOrgId (app disconnected)
  if (alertData.isInstalled === false) {
    alerts.push("missingShopifyOrgId");
  }

  // Script status alerts (only when script status is loaded)
  if (alertData.scriptStatusType) {
    const type = alertData.scriptStatusType;

    // Headless alerts
    if (type === "headlessMissing") {
      alerts.push("headlessMissing");
    } else if (type === "headlessDeprecatedFound") {
      alerts.push("headlessDeprecated");
    } else if (type === "headlessOutdatedFound") {
      alerts.push("headlessOutdated");
    }
    // Normal script alerts (skip if headless store with missing type)
    else if (!(alertData.isHeadless && type === "missing")) {
      if (type === "missing") {
        alerts.push("scriptMissing");
      } else if (type === "isCjs" && alertData.pathname !== "/events-manager") {
        alerts.push("scriptOutdated");
      } else if (type === "multipleScriptsWithAppEmbedFound") {
        alerts.push("scriptMultipleWithAppEmbed");
      } else if (type === "multipleScriptsFound") {
        alerts.push("scriptMultiple");
      } else if (type === "incorrectScriptFound") {
        alerts.push("scriptIncorrect");
      }
    }

    if (
      (type === "inlineFound" || type === "inlineAsyncFound") &&
      (alertData.hasTemplateScript === false ||
        alertData.hasProductThemeScript === false)
    ) {
      alerts.push("scriptIncomplete");
    }

    // Priority 9: CustomEventsESMAlert (route-specific)
    if (type === "isCjs" && alertData.pathname === "/events-manager") {
      alerts.push("customEventsEsm");
    }
  }

  // Priority 8: ShopifyPermissionsAlert
  if (
    alertData.hasRequiredShopifyScopes !== undefined &&
    !alertData.hasRequiredShopifyScopes
  ) {
    alerts.push("shopifyPermissions");
  }

  return alerts;
}

/**
 * Filters alerts based on suppression rules.
 * An alert is suppressed if any of its suppressors are present.
 */
export function filterSuppressedAlerts(rawAlerts: AlertType[]): AlertType[] {
  return rawAlerts.filter((alertType) => {
    const config = ALERT_CONFIGS[alertType];
    return !config.suppressedBy.some((suppressor) =>
      rawAlerts.includes(suppressor)
    );
  });
}

/**
 * Sorts alerts by priority (lower number = higher priority).
 */
export function sortAlertsByPriority(alerts: AlertType[]): AlertType[] {
  return [...alerts].sort(
    (a, b) => ALERT_CONFIGS[a].priority - ALERT_CONFIGS[b].priority
  );
}
